import React from "react";
import { useRelationshipRules } from "../hooks/useRelationshipRules";

const RelationshipViewer = ({
  entityForm,
  entities = [],
  relationships = [],
  editingEntity,
}) => {
  // Helper function to get entity name by ID
  const getEntityName = (entityId) => {
    if (typeof entityId === "string") {
      const entity = entities.find((e) => e.id === entityId);
      return entity ? entity.name : `Unknown (${entityId})`;
    } else if (typeof entityId === "object" && entityId.id) {
      const entity = entities.find((e) => e.id === entityId.id);
      return entity ? entity.name : `Unknown (${entityId.id})`;
    }
    return "Unknown";
  };

  // Get relationships from database for this entity
  const getEntityRelationships = () => {
    const currentEntityId =
      entityForm.id || (editingEntity && editingEntity.id);
    if (!currentEntityId) return [];

    return relationships.filter(
      (rel) =>
        rel.source_entity_id === currentEntityId ||
        rel.target_entity_id === currentEntityId
    );
  };

  const entityRelationships = getEntityRelationships();

  // Get relationship rules for this entity type
  const {
    getRulesForEntityType,
    loading: rulesLoading,
    error: rulesError,
  } = useRelationshipRules();

  const rulesForType = React.useMemo(() => {
    return getRulesForEntityType(entityForm.type) || [];
  }, [entityForm.type, getRulesForEntityType]);

  // Helper function to get section key for a rule (same as EntityRelationships)
  const getSectionKeyForRule = (rule) => {
    if (rule.key === "interface_provided_by_app") {
      return "interface_provided_by_app";
    }
    if (rule.key === "interface_consumed_by_app") {
      return "interface_consumed_by_app";
    }
    // interface_*_by_sc rules are retired with Solution Component.
    if (
      rule.key === "interface_provided_by_sc" ||
      rule.key === "interface_consumed_by_sc"
    ) {
      return null;
    }
    const type = rule.relatedEntityType;
    if (type === "ApplicationInterface") {
      if (rule.key === "interface_provided_by_app") {
        return "interface_provided_by_app";
      } else if (rule.key === "interface_consumed_by_app") {
        return "interface_consumed_by_app";
      } else {
        return rule.direction === "incoming"
          ? "consumed_interfaces"
          : "provided_interfaces";
      }
    }
    if (type === "BusinessCapability") return "related_business_capabilities";
    if (type === "BusinessContext") return "related_business_contexts";
    if (type === "DataObject") return "related_data_objects";
    if (type === "ITComponent") return "related_it_components";
    if (type === "Initiative") return "related_initiatives";
    if (type === "ApplicationComponent") return "related_applications";
    if (type === "BusinessActor") return "related_business_actors";
    if (type === "Organization") return "related_organizations";
    if (type === "Provider") return "related_providers";
    if (type === "Goal") return "related_goals";

    if (rule.key && rule.key.includes("_successor")) {
      return "related_successors";
    }

    return `related_${type.toLowerCase()}s`;
  };

  // Build related entities for each section (same logic as EntityRelationships)
  const buildRelated = (relatedEntityType, sectionKey) => {
    const currentEntityId =
      entityForm.id || (editingEntity && editingEntity.id);
    if (!currentEntityId) return [];

    const rels = relationships.filter(
      (rel) =>
        (rel.source_entity_id === currentEntityId ||
          rel.target_entity_id === currentEntityId) &&
        (rel.type === "Drives" ||
          rel.type === "Is Performed By" ||
          rel.type === "Supports" ||
          rel.type === "Is Delivered By" ||
          rel.type === "Owns/Governs" ||
          rel.type === "Enables/Supports" ||
          rel.type === "Exchanges" ||
          rel.type === "Exposes" ||
          rel.type === "Connects To" ||
          rel.type === "Realizes/Builds" ||
          rel.type === "Runs On" ||
          rel.type === "Is Provided By" ||
          rel.type === "Consumes Service From" ||
          rel.type === "Needs Support From" ||
          rel.type === "Utilizes" ||
          rel.type === "Is Managed By" ||
          // Legacy types for backward compatibility
          rel.type === "Accesses" ||
          rel.type === "Uses" ||
          rel.type === "Serves" ||
          rel.type === "Provides" ||
          rel.type === "Consumes" ||
          rel.type === "Assignment" ||
          rel.type === "Realises" ||
          rel.type === "Data Flow")
    );

    const relatedFromDB = rels
      .map((rel) => {
        const entityId =
          rel.source_entity_id === currentEntityId
            ? rel.target_entity_id
            : rel.source_entity_id;
        const entity = entities.find(
          (e) => e.id === entityId && e.type === relatedEntityType
        );
        if (!entity) return null;
        return {
          ...entity,
          activeFrom: rel.active_from,
          activeUntil: rel.active_until,
          description: rel.description,
          status: rel.status,
          relationshipType: rel.type,
        };
      })
      .filter(Boolean);

    return relatedFromDB;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
        🔗 Relationships
      </h2>

      <div className="space-y-4">
        {/* Parent Entity */}
        {entityForm.parent && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Parent Entity
            </label>
            <div className="bg-gray-700 rounded-lg px-3 py-2">
              <span className="text-gray-300 text-sm">
                {getEntityName(entityForm.parent)}
              </span>
            </div>
          </div>
        )}

        {/* Children Entities */}
        {entityForm.children && entityForm.children.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Children Entities ({entityForm.children.length})
            </label>
            <div className="space-y-1">
              {entityForm.children.map((childId) => (
                <div key={childId} className="bg-gray-700 rounded-lg px-3 py-2">
                  <span className="text-gray-300 text-sm">
                    {getEntityName(childId)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Predecessor Entities */}
        {entityForm.predecessors && entityForm.predecessors.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Predecessor Entities ({entityForm.predecessors.length})
            </label>
            <div className="space-y-1">
              {entityForm.predecessors.map((pred) => {
                const predId = typeof pred === "string" ? pred : pred.id;
                return (
                  <div
                    key={predId}
                    className="bg-gray-700 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm font-medium">
                        {getEntityName(pred)}
                      </span>
                    </div>
                    {typeof pred === "object" &&
                      (pred.activeFrom || pred.activeUntil || pred.comment) && (
                        <div className="mt-2 space-y-1">
                          {pred.activeFrom && (
                            <div className="text-xs text-gray-400">
                              Active from:{" "}
                              {new Date(pred.activeFrom).toLocaleDateString()}
                            </div>
                          )}
                          {pred.activeUntil && (
                            <div className="text-xs text-gray-400">
                              Active until:{" "}
                              {new Date(pred.activeUntil).toLocaleDateString()}
                            </div>
                          )}
                          {pred.comment && (
                            <div className="text-xs text-gray-400">
                              Comment: {pred.comment}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Successor Entities */}
        {entityForm.successors && entityForm.successors.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Successor Entities ({entityForm.successors.length})
            </label>
            <div className="space-y-1">
              {entityForm.successors.map((succ) => {
                const succId = typeof succ === "string" ? succ : succ.id;
                return (
                  <div
                    key={succId}
                    className="bg-gray-700 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm font-medium">
                        {getEntityName(succ)}
                      </span>
                    </div>
                    {typeof succ === "object" &&
                      (succ.activeFrom || succ.activeUntil || succ.comment) && (
                        <div className="mt-2 space-y-1">
                          {succ.activeFrom && (
                            <div className="text-xs text-gray-400">
                              Active from:{" "}
                              {new Date(succ.activeFrom).toLocaleDateString()}
                            </div>
                          )}
                          {succ.activeUntil && (
                            <div className="text-xs text-gray-400">
                              Active until:{" "}
                              {new Date(succ.activeUntil).toLocaleDateString()}
                            </div>
                          )}
                          {succ.comment && (
                            <div className="text-xs text-gray-400">
                              Comment: {succ.comment}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Organized Relationship Sections */}
        {rulesForType.map((rule, index) => {
          const sectionKey = getSectionKeyForRule(rule);
          const relatedEntities = buildRelated(
            rule.relatedEntityType,
            sectionKey
          );

          if (relatedEntities.length === 0) return null;

          return (
            <div key={index}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {rule.visibleLabel} ({relatedEntities.length})
              </label>
              <div className="space-y-2">
                {relatedEntities.map((entity) => (
                  <div
                    key={entity.id}
                    className="bg-gray-700 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-300 text-sm font-medium">
                          {entity.name}
                        </span>
                        <span className="text-gray-400 text-xs px-2 py-1 bg-gray-600 rounded">
                          {entity.relationshipType}
                        </span>
                      </div>
                    </div>
                    {entity.description && (
                      <div className="mt-1 text-xs text-gray-400">
                        {entity.description}
                      </div>
                    )}
                    {(entity.activeFrom ||
                      entity.activeUntil ||
                      entity.status) && (
                      <div className="mt-1 space-y-1">
                        {entity.activeFrom && (
                          <div className="text-xs text-gray-400">
                            Active from:{" "}
                            {new Date(entity.activeFrom).toLocaleDateString()}
                          </div>
                        )}
                        {entity.activeUntil && (
                          <div className="text-xs text-gray-400">
                            Active until:{" "}
                            {new Date(entity.activeUntil).toLocaleDateString()}
                          </div>
                        )}
                        {entity.status && (
                          <div className="text-xs text-gray-400">
                            Status: {entity.status}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Show message if no relationships */}
        {!entityForm.parent &&
          (!entityForm.children || entityForm.children.length === 0) &&
          (!entityForm.predecessors || entityForm.predecessors.length === 0) &&
          (!entityForm.successors || entityForm.successors.length === 0) &&
          rulesForType.every(
            (rule) =>
              buildRelated(rule.relatedEntityType, getSectionKeyForRule(rule))
                .length === 0
          ) && (
            <div className="text-center py-8">
              <div className="text-gray-400 text-sm">
                <div className="text-4xl mb-2">🔗</div>
                <div>No relationships defined for this entity</div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default RelationshipViewer;
